/* Reading what the settings endpoints return (pages/Settings). settingsApi's
   responses are untyped (services/api.ts), so these normalise whatever the
   server sends. */

export type Json = Record<string, unknown>;

export const isRecord = (v: unknown): v is Json => !!v && typeof v === 'object' && !Array.isArray(v);

/** The payload, unwrapped from a lone {settings: {...}} / {data: {...}} envelope. */
export function settingsPayload(data: unknown): Json | null {
    if (!isRecord(data)) return null;
    const keys = Object.keys(data);
    if (keys.length === 1 && isRecord(data[keys[0]])) return data[keys[0]] as Json;
    return data;
}

export const humanize = (key: string) => {
    const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

export interface AgentToken {
    category: string;
    label: string;
    token?: string;
    agents?: number;
    rotatedAt?: string;
}

/** GET /settings/agent-tokens in any of the shapes it may take: a list, a {tokens: [...]} envelope, or a category → token map. */
export function normalizeAgentTokens(data: unknown): AgentToken[] {
    const body = isRecord(data) ? (data.tokens ?? data.agent_tokens ?? data.items ?? data.data ?? data) : data;
    const fromItem = (item: unknown, fallbackKey: string): AgentToken => {
        if (!isRecord(item)) return { category: fallbackKey, label: humanize(fallbackKey), token: item == null ? undefined : String(item) };
        const category = String(item.category ?? item.key ?? item.name ?? item.id ?? fallbackKey);
        const agents = item.agents ?? item.agent_count ?? item.count;
        const rotated = item.rotated_at ?? item.updated_at ?? item.created_at;
        return {
            category,
            label: String(item.label ?? item.display_name ?? item.name ?? humanize(category)),
            token: typeof item.token === 'string' ? item.token : typeof item.value === 'string' ? item.value : undefined,
            agents: typeof agents === 'number' ? agents : undefined,
            rotatedAt: typeof rotated === 'string' ? rotated : undefined,
        };
    };
    if (Array.isArray(body)) return body.map((t, i) => fromItem(t, String(i)));
    if (isRecord(body)) return Object.entries(body).map(([k, v]) => fromItem(v, k));
    return [];
}
